import React from "react";
import { useDispatch, useSelector } from "react-redux";
import get from "lodash.get";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import Accordion from "react-bootstrap/Accordion";
import { useAccordionButton } from "react-bootstrap/AccordionButton";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import domScrollTo from "../utils/domScrollTo";
import ProductsList from "../components/ProductsList";
import useMenuCatalog from "../hooks/useMenuCatalog";
import Avatar from "../components/Avatar";
import { setActiveGroupId } from "../ducks/productsSlice";

/**
 * Cardápio por grupos em sanfona: cada grupo é um cartão com a foto, e o toque
 * abre os produtos dele ali mesmo.
 *
 * - POS: com a fileira de grupos fixa no topo (`showGroupMenu`), que rola até
 *   o grupo e o abre.
 * - Celular, quando a loja liga o layout de grupos (appGrupoLayout): sem a
 *   fileira, abaixo do cabeçalho da loja.
 *
 * O grupo aberto fica no Redux (products.activeGroupId), e não no estado do
 * componente: a lista remonta ao voltar do produto (useGoTo usa replace), e
 * o useScrollMemory só devolve o cliente ao mesmo ponto se o grupo continuar
 * aberto.
 */
export default function GroupedProductsViewInstagram({ showGroupMenu = true }) {
  const dispatch = useDispatch();
  const search = useSelector((state) => state.search);
  const { groups, activeGroupId } = useSelector((state) => state.products);
  const selectedGroupId = activeGroupId ? `${activeGroupId}` : null;
  const setSelectedGroupId = (groupId) =>
    dispatch(
      setActiveGroupId({
        activeGroupId: groupId ? parseInt(groupId, 10) : 0,
      }),
    );
  const {
    productsByGroup: filteredProductsByGroup,
    visibleGroups,
    onProductSelect,
  } = useMenuCatalog(search.content);
  const groupsRefs = React.useRef({});

  groups.forEach((group) => {
    if (!groupsRefs.current[group.id]) {
      groupsRefs.current[group.id] = React.createRef();
    }
  });

  const goToGroup = (groupId) => {
    const groupContainer = groupsRefs.current[groupId];
    if (!groupContainer) {
      return;
    }

    const node = groupContainer.current.parentNode;
    const nodeStyle = window.getComputedStyle(node);
    const paddingTop = nodeStyle.getPropertyValue("padding-top");
    const paddingTopInt = parseInt(paddingTop);

    setSelectedGroupId(groupId.toString());
    setTimeout(() => {
      domScrollTo(
        get(groupContainer.current, "offsetTop", 0) - paddingTopInt - 10,
      );
    }, 350);
  };

  const onSelectAccordion = (groupId) => {
    setSelectedGroupId(groupId);
  };

  return (
    <div>
      {showGroupMenu && (
        <Navbar bg="white" fixed="top">
          <Nav className="nav-instagram-menu">
            {visibleGroups.map((group) => (
              <Nav.Item key={group.id} onClick={() => goToGroup(group.id)}>
                <Nav.Link
                  href="#"
                  className={`d-flex flex-column align-items-center group-menu__item ${
                    group.id.toString() === selectedGroupId ? " fw-bold" : ""
                  }`}
                >
                  <div className="d-flex justify-content-center align-items-center rounded-circle group-menu__image-container">
                    {group.image ? (
                      <div
                        className="rounded-circle group-menu__image"
                        style={{
                          backgroundImage: `url("${group.image}")`,
                        }}
                      />
                    ) : (
                      <Avatar
                        name={group.name}
                        className="bg-danger group-menu__image"
                      />
                    )}
                  </div>
                  {group.name}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </Navbar>
      )}
      <Accordion
        onSelect={onSelectAccordion}
        activeKey={selectedGroupId}
        className={
          showGroupMenu
            ? "grouped-products-instagram"
            : "grouped-products-instagram--inline"
        }
      >
        {visibleGroups.map((group) => (
          <Card
            key={group.id}
            ref={groupsRefs.current[group.id]}
            className="border-0 mb-3"
          >
            <Card.Header
              className="btn-image border-0 p-0 mx-2"
              style={{
                backgroundImage: `linear-gradient(50deg, rgba(0,0,0,1) 0%, ${
                  group.image ? "rgba(255,255,255,0)" : "#dc3545"
                } 55%), url("${group.image}")`,
              }}
            >
              <GroupToggle eventKey={`${group.id}`}>
                <h2 className="fw-bold mt-4 mb-4">{group.name}</h2>
              </GroupToggle>
            </Card.Header>
            <Accordion.Collapse eventKey={`${group.id}`}>
              <Card.Body className="px-0">
                <ProductsList
                  products={filteredProductsByGroup[group.id]}
                  onProductSelect={onProductSelect}
                />
              </Card.Body>
            </Accordion.Collapse>
          </Card>
        ))}
      </Accordion>
    </div>
  );
}

// O Accordion.Toggle saiu no react-bootstrap 2: o botão do cabeçalho usa o
// useAccordionButton, que abre o grupo ou fecha o que já está aberto.
function GroupToggle({ eventKey, children }) {
  const toggle = useAccordionButton(eventKey);
  return (
    <Button
      variant="link"
      className="w-100 text-start text-white btn-image-label"
      onClick={toggle}
    >
      {children}
    </Button>
  );
}
